/**
 * Filename rules for shortcut files, targeting the *intersection* of Windows
 * and macOS rather than whichever OS generated them.
 *
 * The realistic distribution path is a cloud-synced folder (OneDrive, Dropbox,
 * iCloud), so a folder written on a Mac routinely lands on a Windows machine.
 * A name that is merely legal on the OS that produced it is not good enough.
 *
 * Deliberately zero-import: this is the part of the feature with no automated
 * coverage (Playwright can't drive a native folder picker and the frontend has
 * no unit-test runner), so it stays reviewable in isolation.
 */

/**
 * Illegal on Windows. `:` matters on both platforms — it's the classic HFS
 * path separator, and Finder swaps a typed `/` for `:` on disk.
 */
const ILLEGAL_CHARACTERS = new Set([
  '<',
  '>',
  ':',
  '"',
  '/',
  '\\',
  '|',
  '?',
  '*',
]);

/** DEL, and everything below SPACE. Checked by code point rather than by regex. */
function isControlCharacter(character: string): boolean {
  const code = character.codePointAt(0) ?? 0;
  return code < 0x20 || code === 0x7f;
}

/**
 * Windows reserved device names. Matched against the *stem* and
 * case-insensitively, because `CON.url` is reserved just as `CON` is.
 */
const RESERVED_STEMS = /^(?:con|prn|aux|nul|com\d|lpt\d|clock\$)$/i;

/**
 * Cap on the stem, in code points. NTFS allows 255 UTF-16 units and APFS 255
 * UTF-8 bytes, but the binding constraint is Windows' 260-character limit on
 * the *whole* path — and we don't know how deep the user's chosen folder sits.
 * 60 leaves room for the extension, a subfolder segment, and a deep sync root,
 * and still reads well in a Finder or Explorer column.
 */
const MAX_STEM_LENGTH = 60;

/** Used when a label sanitizes away to nothing. Dedup turns repeats into "Page (2)". */
const FALLBACK_STEM = 'Page';

/**
 * Case- and unicode-insensitive identity for collision purposes. Both NTFS and
 * default-configured APFS are case-insensitive, so "ACME" and "Acme" are the
 * same file on disk — deduping case-sensitively means the second write
 * silently clobbers the first.
 */
function collisionKey(stem: string): string {
  return stem.normalize('NFC').toLowerCase();
}

/** Windows silently drops these, which would manufacture collisions after the fact. */
function trimTrailingDotsAndSpaces(value: string): string {
  return value.replace(/[\s.]+$/, '');
}

export function sanitizeShortcutStem(label: string): string {
  const cleaned = [...label]
    .map((character) =>
      ILLEGAL_CHARACTERS.has(character) || isControlCharacter(character)
        ? ' '
        : character
    )
    .join('');

  let stem = cleaned.replaceAll(/\s+/g, ' ').trim();

  // A leading dot hides the file on macOS.
  stem = stem.replace(/^\.+/, '');
  stem = trimTrailingDotsAndSpaces(stem);

  // Spread, not `slice` — `String.prototype.slice` cuts surrogate pairs in
  // half and produces a name the filesystem will reject.
  if ([...stem].length > MAX_STEM_LENGTH) {
    stem = trimTrailingDotsAndSpaces(
      [...stem].slice(0, MAX_STEM_LENGTH).join('')
    );
  }

  if (RESERVED_STEMS.test(stem)) {
    stem = `_${stem}`;
  }

  return stem.length > 0 ? stem : FALLBACK_STEM;
}

/**
 * Suffix collisions the way Explorer and Finder both do natively — " (2)",
 * " (3)" — so a deduped name reads as ordinary rather than as a bug.
 *
 * **Call order matters: pass stems oldest-first.** The pins API is indexed
 * `created_at DESC`, so the list arrives newest-first; deduping in arrival
 * order means adding a Page renumbers files that already exist on disk. Sorted
 * oldest-first, the older Page keeps its unsuffixed name forever.
 *
 * Candidates are checked against names already handed out, so a literal
 * "Acme (2)" in the input can't collide with one this function generated.
 */
export function dedupeStems(stems: string[]): string[] {
  const taken = new Set<string>();

  return stems.map((stem) => {
    let candidate = stem;
    let suffix = 1;

    while (taken.has(collisionKey(candidate))) {
      suffix += 1;
      candidate = `${stem} (${suffix})`;
    }

    taken.add(collisionKey(candidate));
    return candidate;
  });
}
